/* Lawazen — Stamp Finder (homepage widget)
 * -----------------------------------------------------------------------
 * A deliberately short version of the full wizard: what it prints -> shape
 * -> size -> the real stamps that match. From the results it hands off to
 * the full wizard page, pre-answering those three questions via the query
 * string so the customer never repeats themselves.
 *
 * Kept separate from custom-stamp-wizard.js on purpose — this widget stays
 * simple, and a change to the full wizard should never risk the homepage.
 */
(function () {
  'use strict';

  var SHAPES = [
    { key: 'rectangle', label: 'Rectangular' },
    { key: 'square', label: 'Square' },
    { key: 'circle', label: 'Round' },
    { key: 'oval', label: 'Oval' }
  ];

  var CONTENT_TYPES = [
    { key: 'text', label: 'Text or logo', note: 'Your own name, company or artwork' },
    { key: 'date_text', label: 'Date + text', note: 'An adjustable date with your text' },
    { key: 'date', label: 'Date only', note: 'A plain dater' },
    { key: 'number', label: 'Sequential number', note: 'A numbering machine' }
  ];

  var CONTENT_LABEL = {
    text: 'Text or logo', date: 'Date only',
    date_text: 'Date + text', number: 'Sequential number'
  };
  var SHAPE_LABEL = {
    rectangle: 'Rectangular', square: 'Square', circle: 'Round', oval: 'Oval'
  };

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function num(v) { var n = parseFloat(v); return isNaN(n) ? 0 : n; }
  function fmtMm(v) { return String(Math.round(v * 10) / 10); }
  function sizeLabel(shape, w, h) {
    return shape === 'circle' ? 'Ø ' + fmtMm(w) + ' mm' : fmtMm(w) + ' × ' + fmtMm(h) + ' mm';
  }
  function modelOf(title) {
    var t = String(title || '').toUpperCase();
    var m = t.match(/\b2910\s*[\/ ]?(P\d{2}|U\d{1,2})\b/);
    if (m) return '2910 ' + m[1];
    m = t.match(/\b(\d{4,6})\s*\/\s*(PL|L|M|U24)\b/);
    if (m) return m[1] + '/' + m[2];
    var all = t.match(/\b\d{4,6}\b/g);
    return all ? all[all.length - 1] : '';
  }
  function contentAccepts(wanted, productType) {
    if (wanted === 'date') return productType === 'date';
    if (wanted === 'number') return productType === 'number';
    return productType === 'text' || productType === 'date_text';
  }

  function StampFinder(root) {
    this.root = root;
    var dataEl = root.querySelector('[data-finder-json]');
    var parsed = dataEl ? JSON.parse(dataEl.textContent) : { products: [] };
    this.products = (parsed.products || []).filter(function (p) {
      return p.imprintClass && p.contentType && num(p.w) > 0 && num(p.h) > 0;
    });
    this.wizardUrl = root.getAttribute('data-wizard-page-url') || '/pages/design-your-stamp';
    this.state = { contentType: null, shape: null, w: null, h: null };
    this.history = [];
    this.panel = root.querySelector('[data-finder-panel]');
    this.rail = root.querySelector('[data-finder-rail]') || root.querySelector('[data-finder-crumbs]');
    this.hasRendered = false;
    this.goTo('content_type', true);
  }

  StampFinder.prototype.goTo = function (step, replace) {
    if (replace && this.history.length) {
      this.history[this.history.length - 1] = step;
    } else {
      this.history.push(step);
    }
    this.currentStep = step;
    this.render();
    // Never scroll on first render — this widget lives partway down the
    // homepage and must not drag the page to itself on load.
    if (this.hasRendered) {
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    this.hasRendered = true;
  };

  StampFinder.prototype.back = function () {
    if (this.history.length > 1) {
      this.history.pop();
      this.currentStep = this.history[this.history.length - 1];
      this.render();
    }
  };

  StampFinder.prototype.stepLabel = function (step) {
    return { content_type: 'What it prints', shape: 'Shape',
             size: 'Print size', results: 'Your matches' }[step] || step;
  };

  StampFinder.prototype.stepValue = function (step) {
    var s = this.state;
    if (step === 'content_type') return s.contentType ? CONTENT_LABEL[s.contentType] : '';
    if (step === 'shape') return s.shape ? SHAPE_LABEL[s.shape] : '';
    if (step === 'size') return s.w ? sizeLabel(s.shape, s.w, s.h) : '';
    return '';
  };

  StampFinder.prototype.render = function () {
    var self = this;
    if (this.rail) {
      var last = this.history.length - 1;
      this.rail.className = 'sw-rail';
      this.rail.innerHTML = this.history.map(function (step, i) {
        var val = i < last ? self.stepValue(step) : '';
        return '<li class="sw-rail__item' + (i === last ? ' is-active' : ' is-done') + '">' +
          '<span class="sw-rail__num">' + (i + 1) + '</span>' +
          '<span class="sw-rail__text"><span class="sw-rail__label">' + escapeHtml(self.stepLabel(step)) + '</span>' +
          (val ? '<span class="sw-rail__value">' + escapeHtml(val) + '</span>' : '') +
          '</span></li>';
      }).join('');
    }
    var html = '';
    if (this.history.length > 1) {
      html += '<button type="button" class="sw-back" data-nav-back>' +
        '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M10 2 4 8l6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>Back</button>';
    }
    html += '<div class="sw-step">' + this['render_' + this.currentStep]() + '</div>';
    this.panel.innerHTML = html;
    var backBtn = this.panel.querySelector('[data-nav-back]');
    if (backBtn) backBtn.addEventListener('click', function () { self.back(); });
    var fn = this['bind_' + this.currentStep];
    if (fn) fn.call(this);
  };

  StampFinder.prototype.head = function (title, help) {
    return '<h3 class="sw-step-title">' + escapeHtml(title) + '</h3>' +
      (help ? '<p class="sw-step-help">' + escapeHtml(help) + '</p>' : '');
  };

  /* ---------------- content type ---------------- */
  StampFinder.prototype.render_content_type = function () {
    var set = {};
    this.products.forEach(function (p) {
      if (p.contentType === 'date') set.date = true;
      else if (p.contentType === 'number') set.number = true;
      else { set.text = true; set.date_text = true; }
    });
    var types = CONTENT_TYPES.filter(function (c) { return set[c.key]; });
    return this.head('What should the stamp print?') +
      '<div class="sw-choice-grid sw-choice-grid--4">' + types.map(function (c) {
        return '<button type="button" class="sw-card sw-card--choice" data-content="' + c.key + '">' +
          '<span class="sw-card__name">' + escapeHtml(c.label) + '</span>' +
          '<span class="sw-card__note">' + escapeHtml(c.note) + '</span></button>';
      }).join('') + '</div>';
  };
  StampFinder.prototype.bind_content_type = function () {
    var self = this;
    this.panel.querySelectorAll('[data-content]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.contentType = btn.getAttribute('data-content');
        self.goTo('shape');
      });
    });
  };

  /* ---------------- shape ---------------- */
  StampFinder.prototype.shapes = function () {
    var self = this;
    var count = {};
    this.products.forEach(function (p) {
      if (contentAccepts(self.state.contentType, p.contentType)) {
        count[p.imprintClass] = (count[p.imprintClass] || 0) + 1;
      }
    });
    return SHAPES.filter(function (s) { return count[s.key]; })
      .map(function (s) { return { key: s.key, label: s.label, count: count[s.key] }; });
  };
  StampFinder.prototype.render_shape = function () {
    var shapes = this.shapes();
    return this.head('What shape?') +
      '<div class="sw-shape-grid">' + shapes.map(function (s) {
        return '<button type="button" class="sw-card sw-card--shape" data-shape="' + s.key + '">' +
          '<span class="sw-shape-icon sw-shape-icon--' + s.key + '"></span>' +
          '<span class="sw-card__name">' + s.label + '</span>' +
          '<span class="sw-card__note">' + s.count + ' model' + (s.count === 1 ? '' : 's') + '</span></button>';
      }).join('') + '</div>';
  };
  StampFinder.prototype.bind_shape = function () {
    var self = this;
    this.panel.querySelectorAll('[data-shape]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.shape = btn.getAttribute('data-shape');
        self.goTo('size');
      });
    });
  };

  /* ---------------- size ---------------- */
  StampFinder.prototype.sizes = function () {
    var self = this;
    var map = {};
    this.products.forEach(function (p) {
      if (p.imprintClass !== self.state.shape) return;
      if (!contentAccepts(self.state.contentType, p.contentType)) return;
      var w = num(p.w), h = num(p.h);
      var key = w + 'x' + h;
      if (!map[key]) map[key] = { w: w, h: h, count: 0 };
      map[key].count++;
    });
    return Object.keys(map).map(function (k) { return map[k]; })
      .sort(function (a, b) { return (a.w * a.h) - (b.w * b.h); });
  };
  StampFinder.prototype.render_size = function () {
    var sizes = this.sizes();
    var shape = this.state.shape;
    var maxW = Math.max.apply(null, sizes.map(function (s) { return s.w; }));
    var maxH = Math.max.apply(null, sizes.map(function (s) { return s.h; }));
    return this.head('What print size?', 'Drawn to scale relative to each other.') +
      '<div class="sw-size-grid">' + sizes.map(function (s, i) {
        var wPct = Math.max(12, (s.w / maxW) * 100);
        var hPct = Math.max(10, (s.h / maxH) * 100);
        var round = shape === 'circle' ? ' sw-swatch__box--round'
                  : shape === 'oval' ? ' sw-swatch__box--oval' : '';
        return '<button type="button" class="sw-card sw-card--size" data-size="' + i + '">' +
          '<span class="sw-swatch"><span class="sw-swatch__box' + round + '" style="width:' + wPct.toFixed(1) + '%;height:' + hPct.toFixed(1) + '%"></span></span>' +
          '<span class="sw-card__name">' + sizeLabel(shape, s.w, s.h) + '</span></button>';
      }).join('') + '</div>';
  };
  StampFinder.prototype.bind_size = function () {
    var self = this;
    var sizes = this.sizes();
    this.panel.querySelectorAll('[data-size]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var s = sizes[parseInt(btn.getAttribute('data-size'), 10)];
        self.state.w = s.w;
        self.state.h = s.h;
        self.goTo('results');
      });
    });
  };

  /* ---------------- results ---------------- */
  StampFinder.prototype.matches = function () {
    var self = this;
    return this.products.filter(function (p) {
      return contentAccepts(self.state.contentType, p.contentType) &&
        p.imprintClass === self.state.shape &&
        num(p.w) >= self.state.w && num(p.h) >= self.state.h;
    }).sort(function (a, b) { return (num(a.w) * num(a.h)) - (num(b.w) * num(b.h)); });
  };

  StampFinder.prototype.wizardLink = function () {
    var s = this.state;
    var sep = this.wizardUrl.indexOf('?') === -1 ? '?' : '&';
    return this.wizardUrl + sep + 'content=' + encodeURIComponent(s.contentType) +
      '&shape=' + encodeURIComponent(s.shape) + '&w=' + s.w + '&h=' + s.h;
  };

  StampFinder.prototype.render_results = function () {
    var self = this;
    var list = this.matches();
    var s = this.state;
    var designable = s.contentType === 'text' || s.contentType === 'date_text';

    if (!list.length) {
      return this.head('Your matches') +
        '<div class="sw-empty"><p>Nothing in our stock range matches that exactly.</p>' +
        '<p class="sw-empty__sub">Tell us what you need and we will source the right model.</p>' +
        '<a class="button button--secondary" href="/pages/contact">Talk to us</a></div>' +
        '<button type="button" class="sw-restart" data-restart>Start again</button>';
    }

    var head = this.head(
      list.length + ' stamp' + (list.length === 1 ? '' : 's') + ' match your answers',
      designable
        ? 'Pick one and we will take you straight into designing it — your three answers carry over.'
        : 'These are ready-made — nothing to design.');

    return head +
      '<div class="sw-stamp-grid">' + list.map(function (p) {
        var model = modelOf(p.title);
        var exact = num(p.w) === s.w && num(p.h) === s.h;
        return '<div class="sw-card sw-card--stamp sw-card--result">' +
          (exact ? '<span class="sw-badge sw-badge--exact">Exact fit</span>' : '') +
          '<span class="sw-card__media">' + (p.image ? '<img src="' + p.image + '" alt="" loading="lazy">' : '') + '</span>' +
          (model ? '<span class="sw-card__model">' + escapeHtml(model) + '</span>' : '') +
          '<span class="sw-card__name">' + escapeHtml(p.title) + '</span>' +
          '<span class="sw-card__specs">' +
            '<span class="sw-chip">' + sizeLabel(p.imprintClass, num(p.w), num(p.h)) + '</span>' +
            (p.price ? '<span class="sw-chip sw-chip--price">' + escapeHtml(p.price) + ' SAR</span>' : '') +
          '</span>' +
          '<span class="sw-finder-actions">' +
            (designable
              ? '<a class="button sw-finder-link" href="' + self.wizardLink() + '">Customise this stamp</a>'
              : '') +
            '<a class="button button--secondary sw-finder-link" href="' + p.url + '">View product</a>' +
          '</span></div>';
      }).join('') + '</div>' +
      '<button type="button" class="sw-restart" data-restart>Start again</button>';
  };
  StampFinder.prototype.bind_results = function () {
    var self = this;
    var restart = this.panel.querySelector('[data-restart]');
    if (restart) restart.addEventListener('click', function () {
      self.state = { contentType: null, shape: null, w: null, h: null };
      self.history = [];
      self.goTo('content_type', true);
    });
  };

  function init() {
    document.querySelectorAll('[data-stamp-finder]').forEach(function (root) {
      if (root.__sfInit) return;
      root.__sfInit = true;
      new StampFinder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
